import React, { useEffect } from "react";
import CodeBlock from "./CodeBlock";
import docStyles from "../../css/Docs/Docs.module.css";
import { Divider } from "@mui/material";
import DocWrapper from "./DocWrapper";
import { Link } from "react-router-dom";

const CreateDemand = () => {
  useEffect(() => {
    const el = document.getElementById("introduction");
    if (el) {
      el.scrollIntoView();
    }
  }, []);

  const curlSample = `
~~~curl
curl --location --request PUT 'https://test.eqlfin.com/activities/create' 
--header 'Content-Type: application/json' 
--header 'Firebase-Env: env/core' 
--header 'Authorization: Basic {{token}}' 
--data '{
    "share": [],
    "timestamp": 1682899381000,
    "template": "demand",
    "schedule": [
        {
            "name": "Invoice Date",
            "startTime": 1682899381000,
            "endTime": 1682899391000
        }
    ],
    "venue": [],
    "attachment": {
        "tds": {
            "type": "number",
            "value": 0
        },
        "irn": {
            "type": "string",
            "value": ""
        },
        "narration": {
            "type": "string",
            "value": ""
        },
        "dueDate": {
            "type": "string",
            "value": "1688124200000"
        },
        "name": {
            "type": "string",
            "value": "SUN/2023/05"
        },
        "billingDate": {
            "type": "number",
            "value": 1682899381000
        },
        "totalAmount": {
            "type": "number",
            "value": 106000
        },
        "sgst": {
            "type": "number",
            "value": 3000
        },
        "billingCustomer": {
            "type": "gst",
            "value": "CUSTOMER_GST"
        },
        "billingBranch": {
            "type": "gst-office",
            "value": "YOUR_GST"
        },
        "cgst": {
            "type": "number",
            "value": 3000
        },
        "moratium": {
            "type": "number",
            "value": 0
        },
        "igst": {
            "type": "number",
            "value": 0
        }
    },
    "office": "YOUR_OFFICE_NAME"
}'
~~~
`;

  return (
    <DocWrapper>
      <div className={docStyles.container}>
        <section id="introduction" className={docStyles.section}>
          <div className={docStyles.heading}>Introduction</div>
          <div className={docStyles.para}>
            The provided API enables the creation of demands for the requesting
            office. Access to create customers and demands is limited to the
            offices registered with our platform.
          </div>
        </section>

        <br />
        <Divider sx={{ width: "100%" }} />

        <section id="fields" className={docStyles.section}>
          <div className={docStyles.heading}>Important Fields</div>
          <div className={docStyles.para}>
            To create a demand for an existing office, the following fields are
            important:
            <ul
              style={{
                padding: "0px 0px 5px 10px",
                margin: "5px 0px 5px 10px",
              }}
            >
              <li>schedule</li>
              <li>dueDate</li>
              <li>name</li>
              <li>billingDate</li>
              <li>totalAmount</li>
              <li>billingCustomer</li>
              <li>billingBranch</li>
              <li>Office</li>
            </ul>
          </div>
        </section>

        <Divider sx={{ width: "100%" }} />

        <section id="request-example" className={docStyles.section}>
          <div className={docStyles.heading}>Request Example</div>
          <div className={docStyles.para}>
            This is a sample curl request
            <CodeBlock codeMarkdown={curlSample} />
          </div>

          <div className={docStyles.heading} id="schema-description">
            Schema Description
          </div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>share</code>
                </td>
                <td>empty</td>
                <td>array</td>
                <td></td>
              </tr>
              <tr>
                <td>
                  <code>timestamp</code>
                </td>
                <td>1683971979663</td>
                <td>number</td>
                <td>time of the creation</td>
              </tr>
              <tr>
                <td>
                  <code>template</code>
                </td>
                <td>demand</td>
                <td>String</td>
                <td>structure of the API call, here Demand/customer</td>
              </tr>
              <tr>
                <td>
                  <code>venue</code>
                </td>
                <td>empty array</td>
                <td>array</td>
                <td>venue/address of the requesting office</td>
              </tr>
              <tr>
                <td>
                  <code>office</code>
                </td>
                <td>KAVYA COSMETICS</td>
                <td>String</td>
                <td>name of the requesting office</td>
              </tr>
            </tbody>
          </table>
          <div id="schedule" className={docStyles.headingTwo}>
            schedule
          </div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value (default)</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>name</code>
                </td>
                <td>Invoice Date</td>
                <td>string</td>
                <td>date of the invoice added</td>
              </tr>
              <tr>
                <td>
                  <code>startTime</code>
                </td>
                <td>1682899381000</td>
                <td>number</td>
                <td>time of the invoice creation</td>
              </tr>
              <tr>
                <td>
                  <code>endTime</code>
                </td>
                <td>1682899391000</td>
                <td>number</td>
                <td>time of the invoice creation</td>
              </tr>
            </tbody>
          </table>
          <div id="attachments" className={docStyles.headingTwo}>
            attachments
          </div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value (default)</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>sgst</code>
                </td>
                <td>0</td>
                <td>number/int/float</td>
                <td>
                  value of the sgst to be applied depending upon the gst number
                  of the buyer and seller
                </td>
              </tr>
              <tr>
                <td>
                  <code>tds</code>
                </td>
                <td>0</td>
                <td>number/int/float</td>
                <td>tds to be applied</td>
              </tr>
              <tr>
                <td>
                  <code>irn</code>
                </td>
                <td>can be empty</td>
                <td>String</td>
                <td>invoice referrence number</td>
              </tr>
              <tr>
                <td>
                  <code>dueDate</code>
                </td>
                <td>1687804200000</td>
                <td>String</td>
                <td>due date for payment</td>
              </tr>
              <tr>
                <td>
                  <code>cgst</code>
                </td>
                <td>0</td>
                <td>number/int/float</td>
                <td>
                  value of the cgst to be applied depending upon the gst number
                  of the buyer and seller
                </td>
              </tr>
              <tr>
                <td>
                  <code>igst</code>
                </td>
                <td>2500</td>
                <td>number/int/float</td>
                <td>value of the igst to be applied</td>
              </tr>
              <tr>
                <td>
                  <code>billingDate</code>
                </td>
                <td>1682899381000</td>
                <td>number/int/float</td>
                <td>date of the bills added</td>
              </tr>
              <tr>
                <td>
                  <code>totalAmount</code>
                </td>
                <td>207500</td>
                <td>number</td>
                <td>total amount on the invoice</td>
              </tr>
              <tr>
                <td>
                  <code>billingCustomer</code>
                </td>
                <td>GST_NUMBER</td>
                <td>gst number</td>
                <td>gst number of the billing customer/buyer</td>
              </tr>
              <tr>
                <td>
                  <code>moratium</code>
                </td>
                <td>0</td>
                <td>number/int/float</td>
                <td>credit days</td>
              </tr>
              <tr>
                <td>
                  <code>narration</code>
                </td>
                <td>can be empty</td>
                <td>String</td>
                <td>comments added</td>
              </tr>
              <tr>
                <td>
                  <code>billingBranch</code>
                </td>
                <td>GST_NUMBER</td>
                <td>gst-office</td>
                <td>gst number of the requesting office/seller</td>
              </tr>
              <tr>
                <td>
                  <code>name</code>
                </td>
                <td>BILL/2023/06</td>
                <td>String</td>
                <td>vendor code</td>
              </tr>
            </tbody>
          </table>

          <br />
          <Divider sx={{ width: "100%" }} />

          <section id="response" className={docStyles.section}>
            <div className={docStyles.heading}>Response</div>
            <div className={docStyles.para}>
              Upon successful execution of the Create Demand API, the API will
              return a response containing the newly created demand's unique
              identifier.
            </div>

            <div className={docStyles.subHeading}>201 - Created</div>
            <CodeBlock
              codeMarkdown={`
~~~json
{
    "activityId": "wTtWcTBccQDQ8pXRsfLw",
    "message": "",
    "success": true,
    "code": 201
}
~~~
`}
            />
          </section>

          <Divider sx={{ width: "100%" }} />

          <section id="error" className={docStyles.section}>
            <div className={docStyles.heading}>Error Responses</div>

            <div className={docStyles.para}>
              In case of errors during the API call, you may receive one of the
              following error responses:
              <ul
                style={{
                  padding: "0px 0px 5px 10px",
                  margin: "5px 0px 5px 10px",
                }}
              >
                <li>
                  <b>Unauthorized Error: </b> Occurs when the request lacks
                  valid authentication or authorization credentials.
                </li>
                <li>
                  <b> Server Error: </b> Indicates an unexpected error on the
                  server side, such as a database connection issue or an
                  unhandled exception.
                </li>
              </ul>
            </div>

            <div className={docStyles.subHeading}>400 - Bad Request</div>
            <CodeBlock
              codeMarkdown={`
~~~json
{
    "message": "the field of type 'gst-office' must belong to the requesting office only",
    "success": false,
    "code": 400
}
~~~
`}
            />
          </section>
        </section>
      </div>
      <section className={docStyles.tableOfContentSection}>
        <div className={docStyles.tableOfContent}>
          <div className={docStyles.tocHeading}>Table of contents</div>
          <ul className={docStyles.tocList}>
            <li>
              <Link to="#introduction">Introduction</Link>
            </li>
            <li>
              <Link to="#fields">Important Fields</Link>
            </li>
            <li>
              <Link to="#request-example">Request Example</Link>
            </li>
            <li>
              <Link to="#schema-description">Schema Description</Link>
            </li>
            <li>
              <Link to="#response">Response</Link>
            </li>
            <li>
              <Link to="#error">Error Response</Link>
            </li>
          </ul>
        </div>
      </section>
    </DocWrapper>
  );
};

export default CreateDemand;
